// Shared utility functions for challenge modals
import { logger } from '../../lib/utils';
// IMPORTANT: Facebook share URLs use the /api/share/page/ endpoint for dynamic OG meta tags
// The backend's og:url MUST point to the clean app URL so Facebook shows the correct link

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Production URL for sharing - HARDCODED to ensure Facebook sharing always works
// This must match your production domain
const PRODUCTION_URL = 'https://jsblocks.lavidgames.com';

// Cache buster for Facebook sharing - forces Facebook to re-scrape metadata
// Change this value if Facebook is showing stale/cached data
const FB_CACHE_VERSION = 'v15';

// Facebook App ID for Messenger Send Dialog on desktop
// Set REACT_APP_FACEBOOK_APP_ID in .env for direct Messenger sharing on desktop
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

// Direct app URL with challenge parameter (for Twitter, Copy, etc.)
export const getChallengeUrl = (code) => {
  return `${PRODUCTION_URL}?challenge=${code}`;
};

// Facebook share page URL for challenge (with OG meta tags and score card image)
// The share page provides dynamic OG tags but og:url points to clean app URL
export const getFacebookShareUrl = (code) => {
  return `${PRODUCTION_URL}/api/share/page/challenge/${code}?fbv=${FB_CACHE_VERSION}`;
};

// Facebook share page URL for game score (with OG meta tags and score card image)
export const getGameShareUrl = (scoreId) => {
  return `${PRODUCTION_URL}/api/share/page/game/${scoreId}?fbv=${FB_CACHE_VERSION}`;
};

// Facebook share page URL for daily challenge (with OG meta tags and score card image)
export const getDailyShareUrl = (dateStr, playerName = null) => {
  const params = new URLSearchParams();
  params.append('fbv', FB_CACHE_VERSION);
  if (playerName) params.append('player', playerName);
  return `${PRODUCTION_URL}/api/share/page/daily/${dateStr}?${params.toString()}`;
};

// Get production base URL (for general sharing)
export const getProductionUrl = () => {
  return PRODUCTION_URL;
};

export const handleCopyToClipboard = async (text, onSuccess, onError) => {
  try {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    onSuccess?.();
  } catch (error) {
    logger.error('Failed to copy:', error);
    onError?.(text);
  }
};

// Instagram share helper - downloads image and copies text to clipboard
export const handleInstagramShare = async (imageUrl, shareText, onSuccess, onError) => {
  try {
    // Download the image
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'jigsaw-blocks-score.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    // Copy text to clipboard
    await handleCopyToClipboard(shareText, null, null);
    
    onSuccess?.();
  } catch (error) {
    logger.error('Failed to share to Instagram:', error);
    onError?.(error);
  }
};

// Messenger share - opens Messenger with OG-tagged URL for rich scorecard preview
export const handleMessengerShare = (sharePageUrl) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Mobile: Open Messenger app directly via deep link (no App ID needed)
    window.location.href = `fb-messenger://share?link=${encodeURIComponent(sharePageUrl)}`;
  } else if (FACEBOOK_APP_ID) {
    // Desktop with App ID: Use Facebook Send Dialog for direct Messenger sharing
    const redirectUri = encodeURIComponent(window.location.href);
    window.open(
      `https://www.facebook.com/dialog/send?link=${encodeURIComponent(sharePageUrl)}&app_id=${FACEBOOK_APP_ID}&redirect_uri=${redirectUri}`,
      '_blank',
      'width=600,height=500'
    );
  } else {
    // Desktop without App ID: Open Facebook Share dialog (includes "Send in Messenger" option)
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}`,
      '_blank',
      'width=600,height=400'
    );
  }
};

// Web Share API - native share sheet (works on mobile)
export const handleWebShare = async ({ title, text, url }) => {
  if (!navigator.share) return false;
  try {
    await navigator.share({ title, text, url });
    return true;
  } catch (err) {
    if (err.name !== 'AbortError') {
      logger.error('Web Share failed:', err);
    }
    return false;
  }
};

// Check if device is mobile
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
};

// Check if Web Share API is available AND we're on mobile
// (Desktop share sheets have QR codes that don't work cross-platform)
export const canWebShare = () => !!navigator.share && isMobileDevice();

// Facebook share helper - opens Facebook Share dialog with OG-tagged URL
export const handleFacebookShare = (sharePageUrl) => {
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}`,
    '_blank',
    'width=600,height=400'
  );
};

// Get share image URLs - these use PRODUCTION_URL for external sharing (Facebook, etc.)
export const getChallengeShareImageUrl = (code) => {
  return `${PRODUCTION_URL}/api/share/image/challenge/${code}`;
};

// Get preview image URLs - these use the current API (same-origin) for in-app display
// This avoids CORS/ORB issues when previewing images before sharing
export const getChallengePreviewImageUrl = (code) => {
  return `${API}/share/image/challenge/${code}`;
};

export const getDailyShareImageUrl = (dateStr, playerName = null) => {
  const params = playerName ? `?player=${encodeURIComponent(playerName)}` : '';
  return `${PRODUCTION_URL}/api/share/image/daily/${dateStr}${params}`;
};

export const getDailyPreviewImageUrl = (dateStr, playerName = null) => {
  const params = playerName ? `?player=${encodeURIComponent(playerName)}` : '';
  return `${API}/share/image/daily/${dateStr}${params}`;
};

export const getGameShareImageUrl = (scoreId) => {
  return `${PRODUCTION_URL}/api/share/image/game/${scoreId}`;
};

export const getGamePreviewImageUrl = (scoreId) => {
  return `${API}/share/image/game/${scoreId}`;
};

export { API };
